import { isEqual, pick, round } from 'lodash';
import { ExitDetailsFormValues } from '../index';
import { PreviousExitDetails } from '../types';

const CORRECTION_COMPARISON_KEYS = [
  'exitDate',
  'sharePrice',
  'taxableValueSharePrice',
  'taxReliefElegible',
  'considerationType',
  'shareConsiderationCompanyId',
  'shareConsiderationShareClassId',
  'totalConsiderationShares',
  'shareConsiderationValuePerShare',
  'shareConsiderationValueTotal',
  'shareConsiderationEisEligible',
] as const;

/**
 * Builds exit submission data for edit mode
 */
export const buildEditModeSubmissionData = (
  data: ExitDetailsFormValues,
  previousExitDetails: PreviousExitDetails | undefined,
  isBeingCorrected: boolean,
  initialCorrectionValues: ExitDetailsFormValues | null,
) => {
  const { selectedCompany } = previousExitDetails ?? {};

  // Check if form values have changed from initial values for corrections
  let isFormModified = false;
  if (isBeingCorrected && initialCorrectionValues) {
    isFormModified = !isEqual(
      pick(data, CORRECTION_COMPARISON_KEYS),
      pick(initialCorrectionValues, CORRECTION_COMPARISON_KEYS),
    );
  }

  // During correction, ensure taxable and received value totals are always calculated
  // from the explicitly entered share prices, not from any derived or cached values
  const sharesToBeSold = Number(data.sharesToBeSold) || 0;
  const taxableValueSharePrice = Number(data.taxableValueSharePrice) || 0;
  const receivedSharePrice = Number(data.sharePrice) || 0;

  const calculatedTaxableTotal =
    sharesToBeSold > 0 ? round(taxableValueSharePrice * sharesToBeSold, 2) : 0;
  const calculatedReceivedTotal =
    sharesToBeSold > 0 ? round(receivedSharePrice * sharesToBeSold, 2) : 0;

  return {
    ...previousExitDetails,
    selectedCompany,
    exitDate: data.exitDate,
    sharesToBeSold: data.sharesToBeSold,
    considerationType: data.considerationType,
    sharePrice: receivedSharePrice,
    taxableValueSharePrice,
    taxableValueTotal: calculatedTaxableTotal.toString(),
    receivedValueTotal: calculatedReceivedTotal.toString(),
    chargeAccruedFees: data.chargeAccruedFees,
    taxReliefElegible: data.taxReliefElegible,
    taxReliefElegibleAllocationIds: data.taxReliefElegibleAllocationIds,
    shareConsiderationCompanyId: data.shareConsiderationCompanyId,
    shareConsiderationShareClassId: data.shareConsiderationShareClassId,
    totalConsiderationShares: data.totalConsiderationShares
      ? Number(data.totalConsiderationShares)
      : undefined,
    shareConsiderationValuePerShare: data.shareConsiderationValuePerShare
      ? Number(data.shareConsiderationValuePerShare)
      : undefined,
    shareConsiderationValueTotal: data.shareConsiderationValueTotal
      ? Number(data.shareConsiderationValueTotal)
      : undefined,
    shareConsiderationEisEligible: data.shareConsiderationEisEligible,
    shareConsiderationAllocationMethod: data.shareConsiderationAllocationMethod,
    permitPartialShareAllocation: data.permitPartialShareAllocation,
    shareholdingsToBeExited: [],
    isFormModified,
  };
};

/**
 * Builds exit submission data for create mode
 */
export const buildCreateModeSubmissionData = (data: ExitDetailsFormValues) => {
  const sharesToBeSold = Number(data.sharesToBeSold) || 0;
  const taxableValueSharePrice = Number(data.taxableValueSharePrice) || 0;
  const receivedSharePrice = Number(data.sharePrice) || 0;

  const calculatedTaxableTotal =
    sharesToBeSold > 0 ? round(taxableValueSharePrice * sharesToBeSold, 2) : 0;
  const calculatedReceivedTotal =
    sharesToBeSold > 0 ? round(receivedSharePrice * sharesToBeSold, 2) : 0;

  return {
    firmId: data.firmId,
    exitDate: data.exitDate,
    considerationType: data.considerationType,
    selectedCompanyId: data.selectedCompany?._id ?? '',
    selectedCompany: {
      _id: data.selectedCompany?._id ?? '',
      legalName: data.selectedCompany?.legalName ?? '',
      tradingName: data.selectedCompany?.tradingName ?? '',
    },
    sharesToBeSold: data.sharesToBeSold,
    sharePrice: receivedSharePrice,
    taxableValueSharePrice,
    taxableValueTotal: calculatedTaxableTotal.toString(),
    receivedValueTotal: calculatedReceivedTotal.toString(),
    chargeAccruedFees: data.chargeAccruedFees,
    taxReliefElegible: data.taxReliefElegible,
    taxReliefElegibleAllocationIds: data.taxReliefElegibleAllocationIds,
    selectedShareClasses: data.selectedShareClasses ?? [],
    selectedAllocations: data.selectedAllocations,
    shareConsiderationCompanyId: data.shareConsiderationCompanyId,
    shareConsiderationShareClassId: data.shareConsiderationShareClassId,
    totalConsiderationShares: data.totalConsiderationShares
      ? Number(data.totalConsiderationShares)
      : undefined,
    shareConsiderationValuePerShare: data.shareConsiderationValuePerShare
      ? Number(data.shareConsiderationValuePerShare)
      : undefined,
    shareConsiderationValueTotal: data.shareConsiderationValueTotal
      ? Number(data.shareConsiderationValueTotal)
      : undefined,
    shareConsiderationEisEligible: data.shareConsiderationEisEligible,
    shareConsiderationAllocationMethod: data.shareConsiderationAllocationMethod,
    permitPartialShareAllocation: data.permitPartialShareAllocation,
    shareholdingsToBeExited: [],
  };
};
